import React, { useCallback } from 'react';
import * as Select from '@radix-ui/react-select';

import { SortEnum } from '../../../__generated__/globalTypes';

import { ToolbarLayout } from './ToolbarLayout';
import { PageInfo } from './ProductListQueryProvider';
import classes from './Toolbar.scss';

type Props = {
    pageInfo: PageInfo;
    loading: boolean;
    initial: SortValues | undefined;
    setSortOrder(value, order: SortEnum);
    clearSort();
    breadcrumbLabel?: string;
};

export enum SortValues {
    Relevance = 'relevance',
    PriceAsc = 'price-asc',
    PriceDesc = 'price-desc',
    NameAsc = 'name-asc',
    NameDesc = 'name-desc',
}

const sortOptions = [
    {
        value: SortValues.Relevance,
        label: 'Recommended',
    },
    {
        value: SortValues.PriceAsc,
        label: 'Price (Low - High)',
    },
    {
        value: SortValues.PriceDesc,
        label: 'Price (High - Low)',
    },
    {
        value: SortValues.NameAsc,
        label: 'Name (A - Z)',
    },
    {
        value: SortValues.NameDesc,
        label: 'Name (Z - A)',
    },
];

export const Toolbar: React.FunctionComponent<Props> = React.memo(
    ({ setSortOrder, clearSort, initial, pageInfo, loading, breadcrumbLabel }) => {
        const onChange = useCallback(
            (value: string) => {
                switch (value) {
                    case SortValues.NameAsc:
                        return setSortOrder('name', SortEnum.ASC);
                    case SortValues.NameDesc:
                        return setSortOrder('name', SortEnum.DESC);
                    case SortValues.PriceAsc:
                        return setSortOrder('price', SortEnum.ASC);
                    case SortValues.PriceDesc:
                        return setSortOrder('price', SortEnum.DESC);
                    case SortValues.Relevance:
                        return clearSort();
                }
            },
            [clearSort, setSortOrder],
        );

        const currentLabel = sortOptions.find((o) => o.value === initial)?.label ?? sortOptions[0].label;

        return (
            <ToolbarLayout
                loading={loading}
                sorter={
                    <Select.Root defaultValue={initial} onValueChange={onChange}>
                        <Select.Trigger className={classes.toolbarSortSelect} title="Sorter">
                            <div className={classes.label}>
                                <div>Sort by:</div>
                                <div className={classes.labelOption}>
                                    <Select.Value>{currentLabel}</Select.Value>
                                </div>
                            </div>
                        </Select.Trigger>
                        <Select.Portal>
                            <Select.Content
                                className={classes.selectContent}
                                position="popper"
                                side="bottom"
                                align="start"
                                sideOffset={0}
                                avoidCollisions={false}
                            >
                                <Select.Viewport>
                                    {sortOptions.map((option) => (
                                        <Select.Item
                                            key={option.label}
                                            value={option.value}
                                            className={classes.selectItem}
                                        >
                                            <Select.ItemText>{option.label}</Select.ItemText>
                                        </Select.Item>
                                    ))}
                                </Select.Viewport>
                            </Select.Content>
                        </Select.Portal>
                    </Select.Root>
                }
                stats={`Showing page ${pageInfo.current_page} of ${pageInfo.total_pages}${
                    breadcrumbLabel ? ` of ${breadcrumbLabel}` : ''
                }`}
            />
        );
    },
);
